import React, { useState } from 'react';

function permissionClass(permission) {
  const key = (permission || '').trim().toUpperCase();
  if (key === 'EVERYONE') return 'is-everyone';
  if (key === 'MOD' || key === 'MODERATOR' || key === 'MODS') return 'is-mod';
  if (key === 'VIP') return 'is-vip';
  if (key === 'SUB' || key === 'SUBSCRIBER' || key === 'SUBS') return 'is-sub';
  if (key === 'BROADCASTER' || key === 'STREAMER') return 'is-broadcaster';
  return 'is-default';
}

function renderCell(value, style) {
  const text = value ?? '';
  if (!String(text).trim()) {
    return <span className="animus-command-empty">—</span>;
  }

  switch (style) {
    case 'command':
      return <span className="animus-command-chip font-mono">{text}</span>;
    case 'mono':
      return <span className="animus-command-chip animus-command-chip--example font-mono">{text}</span>;
    case 'permission':
      return (
        <span className={`animus-permission-badge ${permissionClass(text)}`}>
          {text}
        </span>
      );
    case 'highlight':
      return <span className="highlight">{text}</span>;
    default:
      return text;
  }
}

function getColumnWidth(style) {
  switch (style) {
    case 'command':
    case 'mono':
      return '12rem';
    case 'permission':
      return '10rem';
    default:
      return null;
  }
}

function TableSeparator({ label }) {
  return (
    <div className="stream-table-separator" role="separator">
      {label ? (
        <span className="stream-table-separator__label font-mono">{label}</span>
      ) : (
        <span className="stream-table-separator__label font-mono">{'// SECTION'}</span>
      )}
      <span className="stream-table-separator__line" aria-hidden="true" />
    </div>
  );
}

function CustomTablePanel({ table }) {
  const columns = table.columns || [];
  const rows = table.rows || [];
  const collapsible = table.collapsible !== false;
  const [open, setOpen] = useState(table.defaultOpen !== false);

  if (!columns.length) return null;

  const panelTitle = (
    <>
      {table.heading}
      {table.highlight ? <> <span className="highlight">{table.highlight}</span></> : null}
    </>
  );

  return (
    <div className="stream-table-block">
      <div className={`animus-commands-panel stream-table-panel${open ? ' is-open' : ' is-collapsed'}`}>
        <div className="animus-commands-panel__chrome" aria-hidden="true">
          <span className="animus-commands-panel__bracket animus-commands-panel__bracket--tl" />
          <span className="animus-commands-panel__bracket animus-commands-panel__bracket--br" />
        </div>

        <div className="animus-commands-panel__head">
          <div className="animus-commands-panel__title">
            <span className="animus-commands-panel__icon" aria-hidden="true">!</span>
            <span>{panelTitle}</span>
          </div>
          {collapsible && (
            <button
              type="button"
              className="animus-commands-panel__toggle"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-label={open ? 'Collapse table' : 'Expand table'}
            >
              <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} aria-hidden="true" />
            </button>
          )}
        </div>

        {open && (
          <div className="animus-commands-panel__body">
            {table.intro && <p className="stream-table-block__intro">{table.intro}</p>}
            <div className="animus-commands-table-wrap">
              <table className="animus-commands-table stream-custom-table">
                <colgroup>
                  {columns.map((col) => {
                    const width = getColumnWidth(col.style);
                    return (
                      <col
                        key={col.key}
                        style={width ? { width } : undefined}
                      />
                    );
                  })}
                </colgroup>
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col.key} scope="col">{col.label || col.key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="stream-custom-table__empty">
                        No rows yet.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        {columns.map((col) => (
                          <td key={col.key} data-label={col.label || col.key}>
                            {renderCell(row[col.key], col.style)}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function StreamCustomTablesSection({ section }) {
  const tables = section.tables || [];

  if (!tables.length) return null;

  return (
    <div className="stream-custom-tables">
      {section.intro && <p className="stream-custom-tables__intro">{section.intro}</p>}
      {tables.map((table, idx) => (
        <React.Fragment key={idx}>
          {table.separatorBefore && (
            <TableSeparator label={table.separatorLabel} />
          )}
          <CustomTablePanel table={table} />
        </React.Fragment>
      ))}
    </div>
  );
}
